import { Card, Gate, Head, Stat, State, useAsync } from "@authoxi/ui-shared";
import { type Report as Data, client, report } from "../api";

/**
 * State of Agent Traffic.
 *
 * **Two states, and the withheld one is the default assumption.** Below ten scanned endpoints
 * the server refuses to publish percentages — a percentage over a sample of three is a number
 * that would be quoted back at us forever. So this page renders the withheld state as a real
 * answer rather than as blanks, and the populated one only when the corpus earns it.
 *
 * Every figure is derived from `checks[]`. Hard-coding the four headline numbers, which is what
 * the original design did, would make them decoration the moment the corpus moved.
 */
export function Report() {
  const { data, loading, error } = useAsync(() => report());

  return (
    <>
      <Head
        pill="Public report"
        title="State of Agent Traffic"
        lede="What we can see across public endpoints: how much agent traffic identifies itself, how much signs, and how much names a human. Published as a page and as JSON."
      />

      {loading && <State kind="loading" title="Reading the corpus…" />}
      {error && <State kind="error" title="Could not load the report" detail={error} />}
      {data && (data.percentages_withheld ? <Withheld data={data} /> : <Published data={data} />)}
    </>
  );
}

function Withheld({ data }: { data: Data }) {
  return (
    <>
      <Card pad="lg">
        <p className="label">Percentages withheld</p>
        <p style={{ margin: "var(--s3) 0 0", color: "var(--ink-2)", maxWidth: "var(--measure)" }}>
          {data.sample_size} endpoint{data.sample_size === 1 ? " has" : "s have"} been checked so
          far. Percentages are withheld until at least ten have been — a share computed over a
          handful of endpoints is a statistic about a population nobody observed, and it would be
          quoted back at us forever.
        </p>
        <p className="sm muted" style={{ marginTop: "var(--s4)" }}>
          The raw counts below are real, and they are what the percentages will be built from.
        </p>
      </Card>
      <Counts data={data} />
    </>
  );
}

function Published({ data }: { data: Data }) {
  const pct = (c: { passed: number; seen: number }) =>
    c.seen === 0 ? 0 : Math.round((c.passed / c.seen) * 100);

  const headline = data.checks.slice(0, 4);
  const grades = ["A", "B", "C", "D", "F"];
  const total = Object.values(data.grades).reduce((a, b) => a + b, 0) || 1;

  return (
    <>
      <div className="stat-grid" data-testid="report-stats">
        {headline.map((c) => (
          <Stat key={c.check_id} n={`${pct(c)}%`} t={c.title.toLowerCase()} />
        ))}
      </div>

      <Card pad="lg" className="">
        <p className="label">Grade distribution across scanned hosts</p>
        <div className="bars" style={{ marginTop: "var(--s4)" }}>
          {grades.map((g) => {
            const n = data.grades[g] ?? 0;
            const share = Math.round((n / total) * 100);
            return (
              <div className="bar-row" key={g}>
                <span>{g}</span>
                <div className="bar">
                  <i style={{ width: `${share * 2.7}%` }} />
                </div>
                <span className="mono muted">{share}%</span>
              </div>
            );
          })}
        </div>
        <p className="sm muted" style={{ marginTop: "var(--s4)" }}>
          Median score {data.median_score} / 100.
        </p>
      </Card>

      <Counts data={data} />

      <Gate
        client={client}
        source="report"
        title="Get the full report"
        body="The raw JSON and the quarter-over-quarter movement. Nothing else sent to this address."
        cta="Send me the report"
      />
    </>
  );
}

function Counts({ data }: { data: Data }) {
  return (
    <>
      <Card pad className="" >
        <p className="label">What was checked, and how many passed</p>
        <div className="stack" style={{ marginTop: "var(--s4)" }}>
          {data.checks.map((c) => (
            <div key={c.check_id}>
              <div className="between" style={{ marginBottom: "var(--s2)" }}>
                <span className="sm">{c.title}</span>
                <span className="sm muted mono">
                  {c.passed} / {c.seen}
                </span>
              </div>
              <div className="meter">
                <i style={{ width: `${c.seen ? (c.passed / c.seen) * 100 : 0}%` }} />
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* The method, next to the numbers rather than in a footnote — the integrity of this
          page depends entirely on it not reading as a census of the internet. */}
      <p className="sm muted" style={{ marginTop: "var(--s4)", maxWidth: "var(--measure)" }}>
        {data.sample_note} We do not report <em>why</em> a host failed to answer — that
        distinction would turn a public scanner into a network-scanning oracle, and the module
        refuses it deliberately.
      </p>
    </>
  );
}
